<script>
import Button from "@/shared/ui/button/Button.vue";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
import { Checkbox } from "@/shared/ui/checkbox";
import { Badge } from "@/shared/ui/badge";

export default {
  name: "NotarialTransactionFilter",
  components: {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Checkbox,
    Badge,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "change", "reset"],
  computed: {
    filters: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    selectedStatusCount() {
      return this.filters.status__in?.length || 0;
    },
    orderByValue: {
      get() {
        const val = this.filters.order_by;
        return val != null && val !== "" ? String(val) : undefined;
      },
      set(value) {
        this.filters.order_by = value;
        this.emitChange();
      },
    },
  },
  methods: {
    emitChange() {
      this.$emit("change");
    },
    onIinInput(key, value) {
      this.filters[key] = (value ?? "")
        .toString()
        .replace(/\D/g, "")
        .slice(0, 12);
      this.emitChange();
    },
    onIinKeypress(e) {
      if (!/[0-9]/.test(e.key)) e.preventDefault();
    },
    onIinPaste(key, e) {
      e.preventDefault();
      const text = (e.clipboardData?.getData("text") || "")
        .replace(/\D/g, "")
        .slice(0, 12);
      this.filters[key] = text;
      this.emitChange();
    },
    resetFilters() {
      this.$emit("reset");
    },
    isStatusSelected(value) {
      return this.filters.status__in?.includes(value) || false;
    },
    toggleStatus(value) {
      const arr = this.filters.status__in || [];
      const idx = arr.indexOf(value);
      if (idx === -1) {
        arr.push(value);
      } else {
        arr.splice(idx, 1);
      }
      this.filters.status__in = [...arr];
      this.emitChange();
    },
  },
};
</script>

<template>
  <div class="filters">
    <div class="overflow-x-auto">
      <div class="filters-row">
        <Input
          v-model="filters.number"
          :placeholder="$t('№ нотариального действия')"
          class="filter-select"
          @input="emitChange"
        />
        <Input
          :model-value="filters.notary_identifier"
          :placeholder="$t('ИИН нотариуса')"
          inputmode="numeric"
          maxlength="12"
          class="filter-select"
          @keypress="onIinKeypress"
          @paste="onIinPaste('notary_identifier', $event)"
          @update:model-value="onIinInput('notary_identifier', $event)"
        />
        <Input
          :model-value="filters.identifier"
          :placeholder="$t('ИИН участника')"
          inputmode="numeric"
          maxlength="12"
          class="filter-select"
          @keypress="onIinKeypress"
          @paste="onIinPaste('identifier', $event)"
          @update:model-value="onIinInput('identifier', $event)"
        />
        <Popover>
          <PopoverTrigger as-child>
            <Button variant="outline" class="filter-select justify-between">
              <span class="text-muted-foreground text-xs">{{
                $t("Статус")
              }}</span>
              <Badge v-if="selectedStatusCount > 0" variant="secondary">
                {{ selectedStatusCount }}
              </Badge>
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-[220px] p-1.5">
            <div class="flex flex-col gap-0.5">
              <label
                v-for="status in statusOptions"
                :key="status.value"
                class="flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer hover:bg-muted"
              >
                <Checkbox
                  size="small"
                  :model-value="isStatusSelected(status.value)"
                  @change="toggleStatus(status.value)"
                />
                <span class="text-sm">{{ status.label }}</span>
              </label>
            </div>
          </PopoverContent>
        </Popover>
        <Select v-model="orderByValue">
          <SelectTrigger
            class="w-full filter-select"
            @clear="orderByValue = undefined"
          >
            <SelectValue :placeholder="$t('Сортировка')" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="-created_at">{{
              $t("По дате (новые)")
            }}</SelectItem>
            <SelectItem value="created_at">{{
              $t("По дате (старые)")
            }}</SelectItem>
            <SelectItem value="number">{{
              $t("По номеру (возр.)")
            }}</SelectItem>
            <SelectItem value="-number">{{
              $t("По номеру (убыв.)")
            }}</SelectItem>
            <SelectItem value="status">{{
              $t("По статусу (возр.)")
            }}</SelectItem>
            <SelectItem value="-status">{{
              $t("По статусу (убыв.)")
            }}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <Button variant="outline" @click="resetFilters">
      {{ $t("Сбросить фильтр") }}
    </Button>
  </div>
</template>

<style scoped lang="scss">
.filters {
  @apply flex justify-between items-center gap-4;
}

.filters-row {
  @apply flex items-center gap-2 w-max;
}

.filter-select {
  min-width: 200px;
}
</style>
